import { Injectable, Type } from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  UntypedFormGroup,
} from '@angular/forms';
import { AuthentificationService } from '@app/authentification/authentification.service';
import * as _ from 'lodash';
import { PrFormContainerComponent } from './pr-form-container/pr-form-container.component';

@Injectable({
  providedIn: 'root',
})
export class PrFormService {
  container: PrFormContainerComponent;

  constructor(private authentificationService: AuthentificationService) {
    this.authentificationService.logoutSubject.subscribe(() =>
      this.container.close(),
    );
  }

  open(type: Type<any>, entity: any, callback?, options?) {
    return this.container.open(type, entity, callback, options);
  }

  trigger(type: Type<any>, entity: any, callback?, options?) {
    let component: any = this.open(type, entity, callback, options);
    component.doneSubject.subscribe(() => {
      this.container.close();
    });
    return component.messageSubject;
  }

  close() {
    this.container.close();
  }

  loading() {
    this.container.loading();
  }

  loaded() {
    this.container.loaded();
  }

  /* VALIDATORS */

  /**
   * @summary Add every `Validator` in parameters to the `FormControl` based on a condition function
   * @params condFn :
   * * Should return false when you want to ignore validators parameters
   * * Should return true when you want to check for the validators parameters
   */
  AddValidatorsBasedOnCondition(
    condFn: (control: AbstractControl) => boolean,
    validators: ValidatorFn | ValidatorFn[],
  ): ValidatorFn {
    return control => {
      // If condFn return false return no error on the input
      if (!condFn(control)) {
        return null;
      }

      // If condFn return true apply every validators in params
      if (!Array.isArray(validators)) {
        return validators(control);
      }

      return validators
        .map(v => v(control))
        .reduce((errors, result) =>
          result === null ? errors : Object.assign(errors || {}, result),
        );
    };
  }

  /**
   * @summary Check if the `target` parameter `FormControl` value is equal to the `predicate` (default = true)
   */
  isTargetControlValueEqualTo(
    target,
    predicate:
      | boolean
      | string
      | string[]
      | ((controlValue: any) => boolean) = true,
  ) {
    return (ctrl: AbstractControl): boolean => {
      let group = <UntypedFormGroup>ctrl.parent;
      if (!group) return false;

      let targetCtrl = group.get(target);

      switch (typeof predicate) {
        case 'function':
          return predicate(targetCtrl?.value);
        case 'object':
          predicate = predicate as string[];
          if (predicate.length && predicate.includes(targetCtrl?.value))
            return true;
          else return false;
        default:
          if (targetCtrl?.value === predicate) return true;
          else return false;
      }
    };
  }

  RefreshEveryValidators(controls?: string[] | string): ValidatorFn {
    return (ctrl: AbstractControl): ValidationErrors => {
      let group = <UntypedFormGroup>ctrl.parent;

      if (!group) {
        return null;
      }

      let currentControl: string;
      let ignoredControl = [];

      Object.keys(group.controls).forEach(key => {
        if (controls && !controls.includes(key))
          ignoredControl.push(group.controls[key]);
        let childControl = group.get(key);

        if (childControl !== ctrl) {
          return;
        }

        currentControl = key;
      });

      _(ctrl.parent.controls)
        .omit(currentControl)
        .filter((c: FormControl) => !ignoredControl.includes(c))
        .forEach((c: FormControl) => c.updateValueAndValidity());

      return null;
    };
  }

  CustomValidator(customError: string): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      return { custom: customError };
    };
  }

  isObjectValidator(control): ValidationErrors | null {
    if (!control.value || typeof control.value !== 'object')
      return { custom: 'Séléctionnez un élément dans la liste' };
    else return null;
  }
}
